import { HustleupSmallIcon } from "@images/svgIcons/hustleUpSmallIcon"
import { word } from "@modules/core/utils/i18n"
import { CurrencyFormattedPrice } from "@modules/currency/components/currencyUtils"
import { ProgramBundleType } from "@modules/programBundle/api/programBundleDto"
import {
  ProgramBundleNoUnfollowModal,
  ProgramBundleNoUnfollowModalRef,
} from "@modules/programBundle/components/programBundleNoUnfollowModal"
import {
  ProgramBundlePauseOnOffModal,
  ProgramBundlePauseOnOffModalRef,
} from "@modules/programBundle/components/programBundlePauseOnOffModal"
import { ProgramBundleLightEntity } from "@modules/programBundle/entities/programBundleEntity"
import { useProgramBundleSidebar } from "@modules/programBundle/hooks/useProgramBundleSidebar"
import { useProgramBundleSubscription } from "@modules/programBundle/hooks/useProgramBundleSubscription"
import { useProgramOnOffProgression } from "@modules/programs/hooks/useProgramsOnOff"
import { ProgramOnOffTypeOf, ProgramProgressionStatus } from "@modules/programs/programOnOffTypes"
import { Program, ProgramSubscriptionStatus } from "@modules/programs/programTypes"
import { HUButton } from "@modules/ui/components/huButton"
import { HUText } from "@modules/ui/components/huText"
import { useMemo, useRef } from "react"
import { useTheme } from "styled-components"

type ProgramBundlePriceInProgramDetailsCardProps = {
  program: Program
  programBundleLight: ProgramBundleLightEntity
  isSubscribedInBothProgramAndBundle: boolean
}

export const ProgramBundlePriceInProgramDetailsCard: React.FC<ProgramBundlePriceInProgramDetailsCardProps> = ({
  program,
  programBundleLight,
  isSubscribedInBothProgramAndBundle,
}) => {
  const programBundleNoUnfollowModalRef = useRef<ProgramBundleNoUnfollowModalRef>(null)
  const programBundlePauseOnOffModalRef = useRef<ProgramBundlePauseOnOffModalRef>(null)

  const theme = useTheme()
  const { navigateToProgramBundleResume } = useProgramBundleSidebar()
  const {
    data: programBundleSubscriber,
    isLoading,
    followProgamsInProgramBundle,
    unFollowProgamsInProgramBundle,
    isPending,
  } = useProgramBundleSubscription(programBundleLight.programBundleId)

  const { data: progression } = useProgramOnOffProgression(
    program.id,
    program._programType !== ProgramOnOffTypeOf ||
      !(
        program.subscriptionStatus === ProgramSubscriptionStatus.ACTIVE ||
        program.subscriptionStatus === ProgramSubscriptionStatus.ACTIVE_BUNDLE ||
        program.subscriptionStatus === ProgramSubscriptionStatus.INACTIVE_BUNDLE ||
        program.subscriptionStatus === ProgramSubscriptionStatus.PENDING_CANCELLATION
      ),
  )

  const canUserFollowProgram = useMemo(
    () => programBundleSubscriber?.canUserFollowBundlePrograms(),
    [programBundleSubscriber],
  )
  const isFollowed = useMemo(
    () =>
      programBundleSubscriber?.programBundle?.programs &&
      programBundleSubscriber.programBundle.programs
        .filter((p) => p.followedInBundle)
        .map((p) => p.id)
        .includes(program.id),
    [programBundleSubscriber],
  )

  const toggleFollow = async () => {
    if (isSubscribedInBothProgramAndBundle) {
      // Tell the user that he can't unfollow the program because he is subscribed to it individually
      programBundleNoUnfollowModalRef.current?.open()
    } else if (
      isFollowed &&
      program._programType === ProgramOnOffTypeOf &&
      progression &&
      progression.status === ProgramProgressionStatus.STARTED
    ) {
      // Tell the user that his progression will be paused if he unfollows the program
      programBundlePauseOnOffModalRef.current?.open()
    } else if (isFollowed) {
      // Unfollow the program
      await unFollowProgamsInProgramBundle([program.id])
    } else {
      // Follow the program
      await followProgamsInProgramBundle([program.id])
    }
  }

  if (!programBundleLight) return null

  return (
    <>
      <div style={{ marginBottom: 7 }}>
        <HUText type="label" color={theme.neutralColor700} fontStyle="TS">
          {programBundleLight.type === ProgramBundleType.VIRTUAL
            ? word("program.subscription.label")
            : word("program.price.label")}
        </HUText>
      </div>

      <div className="card rounded-33 flex flex-column gap-2 overflow-hidden relative">
        <HUText fontStyle="TM">{programBundleLight.programBundleName}</HUText>

        <div className="w-full z-2 gap-2 flex flex-column">
          {programBundleLight.paidPrice?.value ? (
            programBundleLight.type === ProgramBundleType.VIRTUAL ? (
              <div className="flex flex-row align-items-baseline gap-2">
                <CurrencyFormattedPrice price={programBundleLight.paidPrice} />
                <div className="flex ">
                  <HUText fontStyle="LL">{word("program.price.pricePerMonth")}</HUText>
                </div>
              </div>
            ) : (
              <>
                <HUText fontStyle="TM">{word("program.price.singlePurchase")}</HUText>
                <div className="flex flex-row align-items-baseline gap-2">
                  <CurrencyFormattedPrice price={programBundleLight.paidPrice} />
                  <div className="flex">
                    <HUText fontStyle="LL">{word("program.price.onlyOnce")}</HUText>
                  </div>
                </div>
              </>
            )
          ) : (
            <>
              <HUText fontStyle="TM">{word("program.price.free")}</HUText>
              <HUText fontStyle="BM">{word("program.price.freeForAll")}</HUText>
            </>
          )}
        </div>

        <HUText fontStyle="BM" className="mb-2">
          {word("programBundle.details.programPartOfBundle", { bundleName: programBundleLight.programBundleName })}
        </HUText>

        <div className="flex flex-row justify-content-end align-items-center gap-8 z-2">
          {!isLoading && canUserFollowProgram && (
            <HUButton
              size="M"
              type="Default"
              colorType="Underline"
              text={
                isFollowed ? word("programBundle.follow.remove.buttonAlt") : word("programBundle.follow.add.button")
              }
              onClick={toggleFollow}
              loading={isLoading || isPending}
              disabled={isLoading || isPending}
            />
          )}
          <HUButton
            size="M"
            type="Default"
            colorType="Primary"
            text={word("programBundle.details.backToProgramBundle")}
            className="px-4 justify-self-end"
            onClick={() =>
              navigateToProgramBundleResume(programBundleLight.programBundleId, false, { replaceAllComponents: true })
            }
          />
        </div>

        <HustleupSmallIcon
          className="absolute top-0 bg-contain h-full"
          color={theme.mainWhite}
          style={{ opacity: 0.3, right: "-40px" }}
        />

        <ProgramBundleNoUnfollowModal ref={programBundleNoUnfollowModalRef} programType={program._programType} />
        <ProgramBundlePauseOnOffModal
          ref={programBundlePauseOnOffModalRef}
          onConfirmUnfollow={() => unFollowProgamsInProgramBundle([program.id])}
        />
      </div>
    </>
  )
}
